import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import { Lock, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const AdminChangePassword = () => {
  const { admin, changePassword, logout } = useAdminAuth();
  const navigate = useNavigate();
  const forced = !!admin?.must_change_password;

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (novaSenha.length < 8) { setError('A nova senha precisa ter ao menos 8 caracteres.'); return; }
    if (novaSenha !== confirmar) { setError('As duas senhas não conferem.'); return; }
    setSubmitting(true);
    try {
      await changePassword(senhaAtual, novaSenha);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Não foi possível alterar a senha.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md border border-[#E2E8F0] p-6">
        <div className="text-center mb-4">
          <div className="w-12 h-12 mx-auto bg-[#F59E0B] rounded-md flex items-center justify-center mb-3">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-black text-[#0F1C2E]">
            {forced ? 'Defina sua nova senha' : 'Alterar senha'}
          </h1>
          {forced && (
            <p className="text-xs text-[#7C5800] bg-[#FFF8E1] border border-[#FFE082] rounded mt-3 px-3 py-2 inline-flex items-start gap-1.5 text-left">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>Por segurança, é necessário trocar a senha provisória antes de continuar.</span>
            </p>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 p-2.5 mb-3 rounded bg-[#FEE2E2] border border-[#FECACA] text-[#7F1D1D] text-xs" data-testid="admin-change-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-3">
          {[
            ['Senha atual', senhaAtual, setSenhaAtual, 'admin-change-current'],
            ['Nova senha', novaSenha, setNovaSenha, 'admin-change-new'],
            ['Confirmar nova senha', confirmar, setConfirmar, 'admin-change-confirm'],
          ].map(([label, val, set, tid]) => (
            <label key={tid} className="block">
              <span className="text-[11px] font-medium text-[#475569] uppercase tracking-wider">{label}</span>
              <input
                type="password"
                required
                value={val}
                onChange={(e) => set(e.target.value)}
                autoComplete={tid.includes('current') ? 'current-password' : 'new-password'}
                className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm focus:outline-none focus:border-[#1E3A5F]"
                data-testid={tid}
              />
            </label>
          ))}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#006030] hover:bg-[#004020] text-white font-bold py-2 rounded text-sm flex items-center justify-center disabled:opacity-60"
            data-testid="admin-change-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Salvar nova senha</span>
            )}
          </button>
          {!forced && (
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="w-full text-xs text-[#475569] hover:text-[#0F1C2E] mt-1"
            >
              Cancelar
            </button>
          )}
        </form>

        {forced && (
          <button
            type="button"
            onClick={async () => { await logout(); navigate('/admin/login', { replace: true }); }}
            className="mt-4 block text-center text-[11px] text-[#475569] hover:text-[#0F1C2E] underline-offset-2 hover:underline w-full"
          >
            Sair sem alterar
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminChangePassword;
